import React, {useState, useEffect, useRef} from 'react';
import {
    Button,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Box,
    Radio,
} from '@mui/material';
import KeyboardDoubleArrowDownOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowDownOutlined';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { sendJsonToApi } from '../../../API/apiHelper';
import { fetchRixEngineCompareDataFields, fetchRixEngineCompareStatisticFields, fetchRixEngineCompareDimensions } from '../../../API/RixEngineReport';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';


function CompareReportOptionsForm({ onGenerate, onTotalsGet }) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const formattedDate2 = yesterday.toISOString().split('T')[0];

    yesterday.setDate(yesterday.getDate() - 1);
    const formattedDate1 = yesterday.toISOString().split('T')[0];

    const dropDownRef1 = useRef(null);
    const dropDownRef2 = useRef(null);
    const pickerRef1 = useRef(null);
    const pickerRef2 = useRef(null);

    const [options, setOptions] = useState({
        start_date1: formattedDate1,
        end_date1: formattedDate1,
        start_date2: formattedDate2,
        end_date2: formattedDate2,
        fields: [],
        entity: [],
        stats: ['impressions1', 'buyer_net_revenue1', 'fill_rate1', 'rcpm1', 'rcpm_rate_per'],
        dimensions: [],
        csv: 'no',
    });

    const [fields, setFields] = useState([]);
    const [dimensionsList, setDimensionsList] = useState([]);
    const [showCalendar1, setShowCalendar1] = useState(false);
    const [showCalendar2, setShowCalendar2] = useState(false);
    const [selectedRange1, setSelectedRange1] = useState({
        startDate: new Date(options.start_date1),
        endDate: new Date(options.start_date1),
        key: 'selection',
    });

    const [selectedRange2, setSelectedRange2] = useState({
        startDate: new Date(options.start_date2),
        endDate: new Date(options.start_date2),
        key: 'selection',
    });

    const [loading, setLoading] = useState(false);
    const [statistics, setStatistics] = useState([]);

    useEffect(() => {
        const loadFields = async () => {
            const data = await fetchRixEngineCompareDataFields();
            if (Array.isArray(data)) setFields(data);
            else setFields([]);
        };
        const loadStatistics = async () => {
            const data = await fetchRixEngineCompareStatisticFields();
            if (Array.isArray(data)) setStatistics(data);
            else setStatistics([]);
        };
        const loadDimensions = async () => {
            const data = await fetchRixEngineCompareDimensions();
            if (Array.isArray(data)) setDimensionsList(data);
            else setDimensionsList([]);
        };
        loadFields();
        loadStatistics();
        loadDimensions();
    }, []);

    const handleCheckboxChange = (event) => {
        const { name } = event.target;
        setOptions({ ...options, entity: name });
    };

    useEffect(() => {
        const storedValue = Cookies.get('CompareReportRixEngine');
        if (storedValue) {
            try {
                const parsedValue = JSON.parse(storedValue);
                if (parsedValue.stats) setOptions(prev => ({ ...prev, stats: parsedValue.stats }));
                if (parsedValue.entity) setOptions(prev => ({ ...prev, entity: parsedValue.entity }));
                if (parsedValue.start_date1) setOptions(prev => ({ ...prev, start_date1: parsedValue.start_date1 }));
                if (parsedValue.end_date1) setOptions(prev => ({ ...prev, end_date1: parsedValue.end_date1 }));
                if (parsedValue.start_date2) setOptions(prev => ({ ...prev, start_date2: parsedValue.start_date2 }));
                if (parsedValue.end_date2) setOptions(prev => ({ ...prev, end_date2: parsedValue.end_date2 }));
                if (Array.isArray(parsedValue.dimensions)) setOptions(prev => ({ ...prev, dimensions: parsedValue.dimensions }));
            } catch (e) {
                
            }
        } else if (fields.length > 0) {
            const first = fields[0];
            const entityValue = first.value?.value ?? first.value;
            if (entityValue) setOptions(prev => ({ ...prev, entity: entityValue }));
        }
    }, [fields]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            setLoading(true);
            const response = await sendJsonToApi('post', '/api/rixengine/compare/report', options, 'CompareReportRixEngine');

            if (!response?.data) {
                onGenerate(false);
                return;
            }

            if (response.data.totals && Array.isArray(response.data.totals) && response.data.totals.length > 0) {
                onTotalsGet(response.data.totals[0]);
            }

            const compareData = response.data.compareData;
            if (!compareData || (Array.isArray(compareData) && compareData.length === 0)) {
                onGenerate(false);
            } else {
                const fieldsArray = Array.isArray(compareData)
                    ? compareData.map((value, key) => ({ key: String(key), value }))
                    : Object.entries(compareData).map(([key, value]) => ({ key, value }));
                onGenerate(fieldsArray);
            }
        } catch (err) {
            toast.error('An error occurred. Please try again later.');
            console.error(err);
            onGenerate(false);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange1 = (event) => {
        const endDate = new Date(event.selection.endDate);
        endDate.setHours(12, 12, 12, 12);
        const endDateISOString = endDate.toISOString().split('T')[0];
        const startDate = new Date(event.selection.startDate);
        startDate.setHours(12, 12, 12, 12);
        const startDateISOString = startDate.toISOString().split('T')[0];

        setSelectedRange1({
            startDate: new Date(startDateISOString),
            endDate: new Date(endDateISOString),
            key: 'selection',
        });
        setOptions(prev => ({ ...prev, start_date1: startDateISOString, end_date1: endDateISOString }));
    };

    const handleDateChange2 = (event) => {
        const endDate = new Date(event.selection.endDate);
        endDate.setHours(12, 12, 12, 12);
        const endDateISOString = endDate.toISOString().split('T')[0];
        const startDate = new Date(event.selection.startDate);
        startDate.setHours(12, 12, 12, 12);
        const startDateISOString = startDate.toISOString().split('T')[0];

        setSelectedRange2({
            startDate: new Date(startDateISOString),
            endDate: new Date(endDateISOString),
            key: 'selection',
        });
        setOptions(prev => ({ ...prev, start_date2: startDateISOString, end_date2: endDateISOString }));
    };

    const toggleCalendar1 = () => setShowCalendar1(prev => !prev);
    const toggleCalendar2 = () => setShowCalendar2(prev => !prev);

    const handleClickOutside = (event) => {
        if (pickerRef1.current && !pickerRef1.current.contains(event.target) && dropDownRef1.current && !dropDownRef1.current.contains(event.target)) {
            setShowCalendar1(false);
        }
        if (pickerRef2.current && !pickerRef2.current.contains(event.target) && dropDownRef2.current && !dropDownRef2.current.contains(event.target)) {
            setShowCalendar2(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCheckboxChangeStat = (event) => {
        const { name, checked } = event.target;
        let stats = [...options.stats];
        if (checked) stats.push(name);
        else stats = stats.filter(s => s !== name);
        setOptions({ ...options, stats });
    };

    const handleDimensionChange = (event) => {
        const { name, checked } = event.target;
        let dimensions = [...(options.dimensions || [])];
        if (checked) dimensions.push(name);
        else dimensions = dimensions.filter(d => d !== name);
        setOptions({ ...options, dimensions });
    };

    return (
        <Grid container spacing={2} sx={{ width: '100%', marginLeft: '0px' }}>
            <Accordion sx={{ width: '100%' }}>
                <AccordionSummary
                    expandIcon={<KeyboardDoubleArrowDownOutlinedIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{ backgroundColor: '#f0f3f5', width: '100%' }}
                >
                    Options
                </AccordionSummary>
                <AccordionDetails sx={{ width: '100%' }}>
                    <form onSubmit={handleSubmit}>
                        <Grid item xs={12} md={12} sx={{ width: '100%' }}>
                            <Grid item xs={12} md={12} sx={{ width: '100%', margin: '25px 0 25px auto' }}>
                                <Grid item xs={12} md={12}>
                                    {fields.length > 0 ? <strong>Entity</strong> : null}
                                </Grid>
                                {fields.map((field, index) => {
                                    const entityValue = field.value?.value ?? field.value;
                                    const entityLabel = field.value?.label ?? field.value;
                                    return (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Radio
                                                    checked={options.entity === entityValue}
                                                    onChange={handleCheckboxChange}
                                                    name={entityValue}
                                                />
                                            }
                                            label={entityLabel}
                                        />
                                    );
                                })}
                            </Grid>

                            <Grid item xs={12} md={12} sx={{ width: '100%', margin: '25px 0 25px auto' }}>
                                <Grid item xs={12} md={12}>
                                    {dimensionsList.length > 0 ? <strong>Dimensions</strong> : null}
                                </Grid>
                                {dimensionsList.length > 0 ? (
                                    <Grid item xs={12} md={12} sx={{ marginLeft: '0.5rem' }}>
                                        {dimensionsList.map((dim, index) => {
                                            const dimKey = dim.value?.key ?? dim.key;
                                            const dimLabel = dim.value?.value ?? dim.value;
                                            return (
                                                <FormControlLabel
                                                    key={index}
                                                    control={
                                                        <Checkbox
                                                            checked={(options.dimensions || []).includes(dimKey)}
                                                            onChange={handleDimensionChange}
                                                            name={dimKey}
                                                        />
                                                    }
                                                    label={dimLabel}
                                                />
                                            );
                                        })}
                                    </Grid>
                                ) : null}
                            </Grid>

                            <Grid item xs={12} md={12} sx={{ width: '100%', margin: '25px 0 25px auto' }}>
                                <Grid item xs={12} md={12}>
                                    {statistics.length > 0 ? <strong>Statistics</strong> : null}
                                </Grid>
                                {statistics.map((statistic, index) => {
                                    const statKey = statistic.value?.key ?? statistic.key;
                                    const statLabel = statistic.value?.value ?? statistic.value;
                                    return (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Checkbox
                                                    checked={options.stats.includes(statKey)}
                                                    onChange={handleCheckboxChangeStat}
                                                    name={statKey}
                                                />
                                            }
                                            label={statLabel}
                                        />
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </form>
                </AccordionDetails>
            </Accordion>

            <Grid item xs={6} md={6}>
                <Button
                    size="large"
                    startIcon={<BackupTableOutlinedIcon fontSize="small" />}
                    onClick={handleSubmit}
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ marginLeft: '-15px' }}
                >
                    Generate Report
                </Button>
            </Grid>

            <Grid item xs={6} md={6}>
                <div>
                    <div
                        ref={dropDownRef2}
                        style={{
                            userSelect: 'none',
                            borderRadius: '5px',
                            textAlign: 'center',
                            backgroundColor: '#f0f3f5',
                            padding: '10px',
                            cursor: 'pointer',
                            float: 'right',
                            border: '1px solid #dddfe1',
                        }}
                        onClick={toggleCalendar2}
                    >
                        <div>Period II</div>
                        <div>{options.start_date2} - {options.end_date2}</div>
                    </div>
                    {showCalendar2 && (
                        <div ref={pickerRef2} style={{ border: '15px', boxShadow: '15px 15px 20px rgba(0, 0, 0, 0.3)' }}>
                            <DateRangePicker
                                ranges={[selectedRange2]}
                                onChange={handleDateChange2}
                                moveRangeOnFirstSelection={false}
                                showSelectionPreview={true}
                            />
                        </div>
                    )}
                </div>

                <div>
                    <div
                        ref={dropDownRef1}
                        style={{
                            userSelect: 'none',
                            borderRadius: '5px',
                            textAlign: 'center',
                            backgroundColor: '#f0f3f5',
                            padding: '10px',
                            cursor: 'pointer',
                            float: 'right',
                            border: '1px solid #dddfe1',
                            marginRight: '15px',
                        }}
                        onClick={toggleCalendar1}
                    >
                        <div>Period I</div>
                        <div>{options.start_date1} - {options.end_date1}</div>
                    </div>
                    {showCalendar1 && (
                        <div ref={pickerRef1} style={{ border: '15px', boxShadow: '15px 15px 20px rgba(0, 0, 0, 0.3)' }}>
                            <DateRangePicker
                                ranges={[selectedRange1]}
                                onChange={handleDateChange1}
                                moveRangeOnFirstSelection={false}
                                showSelectionPreview={true}
                            />
                        </div>
                    )}
                </div>
            </Grid>

            {loading && (
                <Grid item xs={12} md={12}>
                    <Box justifyContent="center" textAlign="center" alignItems="center">
                        <CircularProgress size={200} />
                    </Box>
                </Grid>
            )}
        </Grid>
    );
}

export default CompareReportOptionsForm;
